/**
 * Gate component that ensures signed-in users have completed their profile
 * and onboarding.
 *
 * Wraps the app and redirects to /onboarding if a signed-in user hasn't
 * created their profile or hasn't completed onboarding.
 */
import { useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useHasProfile, useUserProfile } from '@/hooks/useUserProfile';

interface ProfileGateProps {
  children: React.ReactNode;
}

const EXEMPT_PATHS = ['/terms', '/privacy', '/cookies', '/acceptable-use', '/onboarding'];

export function ProfileGate({ children }: ProfileGateProps) {
  const { pathname } = useLocation();
  const { isSignedIn, isLoaded } = useAuth();
  const { data: profileCheck, isLoading: isCheckingProfile } = useHasProfile();
  const { data: profile, isLoading: isLoadingProfile } = useUserProfile();

  // Don't block rendering until we know the auth state
  if (!isLoaded || isCheckingProfile || isLoadingProfile) {
    return <>{children}</>;
  }

  const isExempt = EXEMPT_PATHS.includes(pathname);

  // Redirect to onboarding if no profile or onboarding incomplete
  if (isSignedIn && profileCheck && !isExempt) {
    if (!profileCheck.has_profile || (profile && !profile.has_completed_onboarding)) {
      return <Navigate to="/onboarding" state={{ returnTo: pathname }} replace />;
    }
  }

  return <>{children}</>;
}
